import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AttachFilesSection } from '@/components/shared'
import type { AttachmentRef } from '@/components/shared'
import { jobsApi } from '@/services/jobs'
import { clientsApi } from '@/services/clients'
import { settingsApi } from '@/services/settings'
import { getApiErrorMessage } from '@/services/api'
import { toast } from '@/components/ui/toast'
import { usePermissions } from '@/hooks/usePermissions'
import { DOCUMENT_FILE_ACCEPT } from '@/lib/constants'

interface Props {
  jobId: string
  clientId?: string
}

function sameRef(
  firstAttachment: AttachmentRef,
  secondAttachment: AttachmentRef
): boolean {
  if (firstAttachment.file_id && secondAttachment.file_id) {
    return firstAttachment.file_id === secondAttachment.file_id
  }
  if (firstAttachment.id && secondAttachment.id) {
    return firstAttachment.id === secondAttachment.id
  }
  return (
    firstAttachment.name === secondAttachment.name &&
    (firstAttachment.url ?? '') === (secondAttachment.url ?? '')
  )
}

function attachmentSource(
  docType: string | null | undefined
): AttachmentRef['source'] {
  if (docType === 'client' || docType === 'library') return docType
  return 'upload'
}

// Job attachments tab (2.3.7). Wraps the shared AttachFilesSection, wiring it to
// the job document endpoints (upload new / pick from the client's documents /
// pick from the Settings document library). Persists each change immediately.
export function JobAttachments({ jobId, clientId }: Props) {
  const { t } = useTranslation('jobs')
  const { canEdit } = usePermissions()
  const readOnly = !canEdit('agenda')
  const [attachments, setAttachments] = useState<AttachmentRef[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let active = true
    jobsApi
      .documents(jobId, { limit: 100 })
      .then(res => {
        if (!active) return
        setAttachments(
          (res.data ?? []).map(document => ({
            id: document.id,
            file_id: document.file_id,
            name: document.file_name ?? t('attachments.untitled'),
            url: document.url,
            mime_type: document.file_type,
            size: document.file_size,
            created_at: document.created_at,
            source: attachmentSource(document.doc_type),
          }))
        )
      })
      .catch(error => {
        if (!active) return
        toast({
          variant: 'destructive',
          title: getApiErrorMessage(error, t('attachments.loadError')),
        })
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [jobId, t])

  const uploadFn = useCallback(
    async (file: File): Promise<AttachmentRef> => {
      const res = await jobsApi.uploadDocument(jobId, file)
      const document = res.data!
      return {
        id: document.id,
        file_id: document.file_id,
        name: document.file_name ?? file.name,
        url: document.url,
        mime_type: document.file_type,
        size: document.file_size,
        created_at: document.created_at,
        source: 'upload',
      }
    },
    [jobId]
  )

  const listClientDocsFn = useCallback(
    async (clientId: string): Promise<AttachmentRef[]> => {
      const res = await clientsApi.documents(clientId, { limit: 100 })
      return (res.data ?? []).map(document => ({
        id: document.file_id,
        file_id: document.file_id,
        name: document.file_name,
        url: document.url,
        mime_type: document.file_type,
        size: document.file_size,
        created_at: document.created_at,
        source: 'client' as const,
      }))
    },
    []
  )

  const listLibraryFn = useCallback(async (): Promise<AttachmentRef[]> => {
    const res = await settingsApi.reusableDocumentOptions('job')
    return (res.data ?? []).map(document => ({
      id: document.file_id,
      file_id: document.file_id,
      name: document.name,
      created_at: document.created_at,
      source: 'library' as const,
    }))
  }, [])

  const onChange = useCallback(
    async (next: AttachmentRef[]) => {
      const prev = attachments
      setAttachments(next)

      const added = next.filter(n => !prev.some(p => sameRef(p, n)))
      const removed = prev.filter(p => !next.some(n => sameRef(p, n)))

      try {
        for (const ref of added) {
          if (ref.source === 'upload') continue
          if (!ref.id) continue
          const res = await jobsApi.linkDocument(jobId, ref.id, ref.source)
          setAttachments(currentAttachments =>
            currentAttachments.map(current =>
              sameRef(current, ref) ? { ...current, id: res.data?.id } : current
            )
          )
        }
        for (const ref of removed) {
          if (ref.id) await jobsApi.deleteDocument(jobId, ref.id)
        }
      } catch (e) {
        setAttachments(prev)
        toast({
          variant: 'destructive',
          title: getApiErrorMessage(e, t('attachments.error')),
        })
      }
    },
    [jobId, attachments, t]
  )

  const onRename = useCallback(
    async (item: AttachmentRef, name: string) => {
      if (item.source !== 'upload' || !item.id) return
      const response = await jobsApi.renameDocument(jobId, item.id, name)
      toast({ variant: 'success', title: t('attachments.renameSuccess') })
      return {
        name: response.data?.file_name ?? item.name,
        url: response.data?.url ?? item.url,
      }
    },
    [jobId, t]
  )

  return (
    <AttachFilesSection
      value={attachments}
      onChange={onChange}
      readOnly={readOnly}
      clientId={clientId}
      accept={DOCUMENT_FILE_ACCEPT}
      uploadFn={uploadFn}
      listClientDocsFn={clientId ? listClientDocsFn : undefined}
      listLibraryFn={listLibraryFn}
      libraryTarget='job'
      preloadLibrary
      onRename={onRename}
      hideHeading
      brandTabs
      emptyMessage={loading ? undefined : t('attachments.emptyTitle')}
      emptyDescription={loading ? undefined : t('attachments.emptyDescription')}
      emptyMessageClassName='text-sm'
      emptyDescriptionClassName='text-xs leading-4'
    />
  )
}

export default JobAttachments
